import { useState, useEffect, useCallback } from 'react';
import { socialLinkAPI } from '../services/api';

const PLATFORMS = ['YouTube', 'Instagram', 'WhatsApp', 'Facebook', 'LinkedIn', 'Twitter', 'Telegram', 'Other'];

const s = {
  container: { width: '100%' },
  header: { marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0 },
  subtitle: { fontSize: '13px', color: '#64748b', marginTop: '4px' },
  card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
  toolbar: { display: 'flex', justifyContent: 'flex-end', padding: '16px', borderBottom: '1px solid #e2e8f0' },
  addBtn: { padding: '10px 20px', backgroundColor: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '16px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#1e293b', verticalAlign: 'middle' },
  badge: { padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', cursor: 'pointer' },
  active: { backgroundColor: '#dcfce7', color: '#166534' },
  inactive: { backgroundColor: '#fee2e2', color: '#991b1b' },
  iconBtn: { padding: '8px', border: 'none', borderRadius: '6px', backgroundColor: 'transparent', cursor: 'pointer', color: '#64748b' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalBox: { backgroundColor: '#fff', borderRadius: '16px', width: '90%', maxWidth: '480px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  modalHead: { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalBody: { padding: '24px', overflowY: 'auto', flex: 1 },
  modalFoot: { padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  select: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', backgroundColor: '#fff' },
  btn: { padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', border: 'none' },
  primary: { backgroundColor: '#4f46e5', color: '#fff' },
  secondary: { backgroundColor: '#f1f5f9', color: '#475569' },
  error: { padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', marginBottom: '16px', fontSize: '14px' },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#64748b' },
};

const SocialLinks = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ platform: 'YouTube', url: '', icon: '', sortOrder: 0, isActive: true });
  const [formErr, setFormErr] = useState('');
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    try { setLoading(true); const r = await socialLinkAPI.getAll(); setItems(r.data.data || []); }
    catch { setError('Failed to fetch'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const openCreate = () => { setEditing(null); setForm({ platform: 'YouTube', url: '', icon: '', sortOrder: items.length + 1, isActive: true }); setFormErr(''); setShowModal(true); };
  const openEdit = (item) => { setEditing(item); setForm({ platform: item.platform, url: item.url, icon: item.icon || '', sortOrder: item.sortOrder, isActive: item.isActive }); setFormErr(''); setShowModal(true); };

  const handleSubmit = async () => {
    if (!form.platform || !form.url) { setFormErr('Platform and URL are required'); return; }
    try { setSaving(true); editing ? await socialLinkAPI.update(editing.id, form) : await socialLinkAPI.create(form); setShowModal(false); fetch(); }
    catch (e) { setFormErr(e.response?.data?.error || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div><h1 style={s.title}>Social Links</h1><p style={s.subtitle}>Social media icons shown in the website footer</p></div>
      </div>
      {error && <div style={s.error}>{error}</div>}
      <div style={s.card}>
        <div style={s.toolbar}><button style={s.addBtn} onClick={openCreate}>+ Add Social Link</button></div>
        {loading ? <div style={s.empty}>Loading...</div> : items.length === 0 ? <div style={s.empty}>No social links yet</div> : (
          <table style={s.table}>
            <thead><tr>
              <th style={{ ...s.th, width: '60px' }}>Order</th>
              <th style={s.th}>Platform</th>
              <th style={s.th}>URL</th>
              <th style={{ ...s.th, width: '100px' }}>Status</th>
              <th style={{ ...s.th, width: '100px' }}>Actions</th>
            </tr></thead>
            <tbody>{items.map(item => (
              <tr key={item.id}>
                <td style={s.td}>{item.sortOrder}</td>
                <td style={{ ...s.td, fontWeight: '600' }}>{item.platform}</td>
                <td style={s.td}><a href={item.url} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', fontSize: '13px', wordBreak: 'break-all' }}>{item.url}</a></td>
                <td style={s.td}><span style={{ ...s.badge, ...(item.isActive ? s.active : s.inactive) }} onClick={async () => { await socialLinkAPI.toggleActive(item.id); fetch(); }}>{item.isActive ? 'Active' : 'Inactive'}</span></td>
                <td style={s.td}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button style={s.iconBtn} onClick={() => openEdit(item)}><svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg></button>
                    <button style={{ ...s.iconBtn, color: '#dc2626' }} onClick={async () => { if (window.confirm('Delete?')) { await socialLinkAPI.delete(item.id); fetch(); } }}><svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div style={s.modal} onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div style={s.modalBox}>
            <div style={s.modalHead}>
              <span style={{ fontSize: '18px', fontWeight: '600' }}>{editing ? 'Edit Social Link' : 'Add Social Link'}</span>
              <button style={s.iconBtn} onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div style={s.modalBody}>
              {formErr && <div style={s.error}>{formErr}</div>}
              <div style={{ marginBottom: '16px' }}>
                <label style={s.label}>Platform *</label>
                <select style={s.select} value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })}>
                  {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={s.label}>URL *</label>
                <input style={s.input} value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://youtube.com/@yourchannel" />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={s.label}>Custom Icon SVG Path (optional)</label>
                <input style={s.input} value={form.icon} onChange={e => setForm({ ...form, icon: e.target.value })} placeholder="Leave empty for default icon" />
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>SVG path data for the icon. Leave empty to use the built-in icon for the platform.</div>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}><label style={s.label}>Sort Order</label><input style={s.input} type="number" value={form.sortOrder} onChange={e => setForm({ ...form, sortOrder: Number(e.target.value) })} /></div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', paddingBottom: '4px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} /><span style={{ fontSize: '14px' }}>Active</span>
                  </label>
                </div>
              </div>
            </div>
            <div style={s.modalFoot}>
              <button style={{ ...s.btn, ...s.secondary }} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={{ ...s.btn, ...s.primary, opacity: saving ? 0.6 : 1 }} onClick={handleSubmit} disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialLinks;
